import React from 'react';
import { RiPlaneLine, RiBuildingLine, RiMedalLine, RiCheckLine, RiPriceTag3Line, RiTrophyLine, RiLoader4Line, RiSendPlane2Line, RiDraftLine, RiCalendar2Line, RiTeamLine, RiMapPinLine, RiTimeLine, RiFlightTakeoffLine, RiFlightLandLine, RiArrowRightLine, RiCalendarLine, RiCalendarScheduleLine } from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle
} from '../../../../components/ui/Drawer';
import ImageCarousel from '@components/ui/ImageCarousel';
import { trunc2 } from '@/utils';
import { getAircraftModelName, getOperatorWithLocation, getAircraftPhotos } from '@/features/tails/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS } from '@/lib/permissions';

// "Saturday, October 24, 2025" — long form for the Departure Date summary row.
const formatLongDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
};

// "24 Oct" — compact form for the per-leg rows.
const formatShortDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

/**
 * QuotePreview Component
 * Sidebar drawer preview of complete quote before sending
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether drawer is open
 * @param {Function} props.onClose - Callback to close drawer
 * @param {Object} props.quoteData - Complete quote data
 * @param {Function} props.onSend - Callback to send quote
 * @param {Function} props.onSaveDraft - Callback to save quote as draft
 * @param {boolean} props.isSending - Whether quote is being sent
 * @param {boolean} props.isSavingDraft - Whether draft is being saved
 */
const QuotePreview = ({
  isOpen = false,
  onClose,
  quoteData,
  onSend,
  onSaveDraft,
  isSending = false,
  isSavingDraft = false,
  hideSaveDraft = false,
}) => {

  const { can } = usePermissions();
  const canSendQuote = can(MODULES.BOOKING, ACTIONS.SEND_QUOTE);

  // Safe data access
  const client = quoteData?.client?.clientData || {};
  const flights = quoteData?.flights || [];
  const aircraft = quoteData?.aircraft?.aircraft;
  const settings = quoteData?.settings || {};
  const flightType = quoteData?.flightType || 'one-way';
  const operator = quoteData?.aircraft?.operator || {};
  const bidOptions = quoteData?.bidOptions || [];

  // Handle send
  const handleSend = () => {
    if (onSend && !isSending) {
      const totalCost = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.final_price || option.total_price) || 0), 0) || 0);
      const operatorCost = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.operator_cost || option.total_price) || 0), 0) || 0);
      const fet = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.federal_excise_tax) || 0), 0) || 0);
      const platformFee = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.platform_fee) || 0), 0) || 0);
      const segtax = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.segment_tax) || 0), 0) || 0);
      const passengers = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.segment_pax_count) || 0), 0) || 0;
      
      const brokerMargin = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.broker_markup) || 0), 0) || 0);
      const cardFee = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.credit_card_fee) || 0), 0) || 0);

      quoteData.settings.total_cost = totalCost;
      quoteData.settings.operator_cost = operatorCost;
      quoteData.settings.platform_fee = platformFee;
      quoteData.settings.broker_margin = brokerMargin;
      quoteData.settings.federal_excise_tax = fet;
      quoteData.settings.segment_tax = segtax;
      quoteData.settings.total_passengers = passengers;
      quoteData.settings.card_fee = cardFee;
      quoteData.settings.taxable_amount = operatorCost + brokerMargin;
      onSend(quoteData);
    }
  };

  // Handle save draft
  const handleSaveDraft = () => {
    if (onSaveDraft && !isSending) {
      const totalCost = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.final_price || option.total_price) || 0), 0) || 0);
      const operatorCost = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.operator_cost || option.total_price) || 0), 0) || 0);
      const fet = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.federal_excise_tax) || 0), 0) || 0);
      const platformFee = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.platform_fee) || 0), 0) || 0);
      const segtax = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.segment_tax) || 0), 0) || 0);
      const passengers = bidOptions?.reduce((sum, option) => sum + (parseFloat(option.segment_pax_count) || 0), 0) || 0;
      
      const brokerMargin = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.broker_markup) || 0), 0) || 0);
      const cardFee = trunc2(bidOptions?.reduce((sum, option) => sum + (parseFloat(option.credit_card_fee) || 0), 0) || 0);

      quoteData.settings.total_cost = totalCost;
      quoteData.settings.operator_cost = operatorCost;
      quoteData.settings.platform_fee = platformFee;
      quoteData.settings.broker_margin = brokerMargin;
      quoteData.settings.federal_excise_tax = fet;
      quoteData.settings.segment_tax = segtax;
      quoteData.settings.total_passengers = passengers;
      quoteData.settings.card_fee = cardFee;
      quoteData.settings.taxable_amount = operatorCost + brokerMargin;
      onSaveDraft(quoteData);
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent side="right" className="w-full max-w-md lg:max-w-2xl flex flex-col" closeOnOutsideClick={false}>
        <DrawerHeader className="sr-only">
          <DrawerTitle>Quote Preview</DrawerTitle>
        </DrawerHeader>
        {/* Header */}
        <div className="flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex-1">
            <h2 className="font-semibold text-gray-900 text-2xl">
              {settings.bidTitle || 'Miami Trip (TEB - MIA)'}
            </h2>
            <p className="text-lg text-gray-500 mt-0.5">
              Quote for {client.first_name || client.firstName} {client.last_name || client.lastName}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 bg-gray-100 px-1 rounded-lg mt-3 mr-[-15px]">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pb-24">

          {/* Trip Details Section */}
          <div className="pr-2 pt-4 pb-6">
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4">
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">TRIP DETAILS</h3>

                {/* Row 1 — Passenger Count + Trip Type (first leg pair) */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Passenger Count */}
                  <div className="flex items-start gap-2.5">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                      <RiTeamLine className="h-4 w-4 text-gray-600"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-1">Passenger Count</p>
                      <p className="text-base font-semibold text-gray-900">
                        {flights[0]?.passengerCount || 6}
                      </p>
                    </div>
                  </div>

                  {/* Trip Type — for multi-leg, span both columns on mobile only so the airport list has room */}
                  <div className={`flex items-start gap-2.5${flightType === 'multi-leg' && flights.length > 1 ? ' col-span-2 md:col-span-1' : ''}`}>
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                      <RiMapPinLine className="h-4 w-4 text-gray-600"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-1">
                        {flightType === 'round-trip' ? 'Round Trip' : flightType === 'multi-leg' ? 'Multi-Leg' : 'One-Way'}
                      </p>
                      <p className="text-base font-semibold text-gray-900">
                        {flightType === 'multi-leg' && flights.length > 1
                          ? flights.map((leg, i, arr) => (
                              <span key={i} className="inline-block whitespace-nowrap">
                                {leg.departure?.icao} → {leg.arrival?.icao}{i < arr.length - 1 ? ', ' : ''}
                              </span>
                            ))
                          : <>{flights[0]?.departure?.icao} → {flights[0]?.arrival?.icao}</>
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Row 2 — Departure Date (full width, first flight's date) */}
                <div className="flex items-start gap-2.5 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                    <RiCalendar2Line className="h-4 w-4 text-gray-600"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-600 mb-1">Departure Date</p>
                    <p className="text-base font-semibold text-gray-900">
                      {formatLongDate(flights[0]?.departureDate)}
                    </p>
                  </div>
                </div>

                {/* Per-leg compact rows, separated by dividers */}
                <div className="space-y-2 border-t border-gray-100 pt-2">
                  {flights.map((flight, i) => (
                    <div key={`ml-${i}`} className="flex items-center justify-between border-b border-gray-100 pb-2">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightTakeoffLine className="w-4 h-4" /> {flight.departure?.icao}</span>
                          <RiArrowRightLine className="w-4 h-4 text-gray-400" />
                          <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightLandLine className="w-4 h-4" />{flight.arrival?.icao}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <RiCalendarLine className="w-3.5 h-3.5" />
                            <span>{formatShortDate(flight.departureDate)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <RiTimeLine className="w-3.5 h-3.5" />
                            <span>{flight.departureTime}</span>
                          </div>
                        </div>
                      </div>
                  ))}
                </div>
              </div>

              {/* Expiration Bar */}
              <div className="bg-gray-100 px-4 py-1.5 flex items-center justify-center gap-1.5 mx-4 mb-4 rounded-2xl">
                <RiCalendar2Line className="w-3.5 h-3.5 text-gray-500" />
                <p className="text-xs text-gray-600">
                  Expires on {new Date(settings.validUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>

          {/* Aircraft Section */}
          {bidOptions && bidOptions.length > 0 && (
            <div className="pr-2 pb-6">
              {bidOptions
                .filter(option => option.shortlisted === true)
                .map((option, index) => {
                  const optionAircraft = option.aircraft;
                  const optionOperator = option.aircraft.operator || {};
                  const features = option.aircraft.aircraft_features || {};
                  return (
                  <div key={option.bid_option_id || index} className="border border-gray-200 rounded-2xl mb-4">
                    {/* Aircraft Image */}
                    <div className="rounded-t-2xl overflow-hidden">
                      <ImageCarousel
                        images={(() => {
                          const photos = getAircraftPhotos(optionAircraft, true);
                          return Array.isArray(photos) ? photos : [photos];
                        })()}
                        alt={getAircraftModelName(optionAircraft)}
                        className="h-[350px]"
                      />
                    </div>

                    {/* Aircraft Details */}
                    <div className="px-4 pb-3 pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold text-gray-900">{getAircraftModelName(optionAircraft)}</h3>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <RiPlaneLine className="h-5 w-5"/>
                            {optionAircraft?.tail_number}
                          </p>
                        </div>
                        {/*
                        <TrustScore score={optionOperator?.trust_score || 0} size="sm" />
                        */}
                      </div>

                      {/* Operator */}
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                        <RiBuildingLine className="h-5 w-5"/>
                        <span>{getOperatorWithLocation(optionOperator)}</span>
                      </div>

                      {/* Price */}
                      <div className="inline-flex items-center px-2 py-1 rounded-2xl gap-2 mb-4 bg-accent">
                        <RiPriceTag3Line className="h-5 w-5 font-bold text-[#0C3E71]" />
                        <span className="text-md font-normal text-[#0C3E71]">{trunc2(option.final_price || option.total_price)}</span>
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {optionOperator?.argus_rating && optionOperator?.argus_rating!="No" &&
                          <span className={`px-2.5 py-1 bg-gray-200 text-[#171717] text-xs font-medium rounded-2xl flex items-center gap-1`}>
                            <RiTrophyLine className="h-5 w-5 text-gray-600"/> {optionOperator?.argus_rating.toUpperCase()}
                          </span>}
                          
                          {optionOperator?.wyvern_rating && optionOperator?.wyvern_rating!="No" &&
                          <span className={`px-2.5 py-1 bg-gray-200 text-[#171717] text-xs font-medium rounded-2xl flex items-center gap-1`}>
                            <RiMedalLine className="h-4 w-4 text-gray-600"/> WINGMAN
                          </span>
                          }

                          {features?.no_aog &&
                          <span className={`px-2.5 py-1 bg-gray-200 text-[#171717] text-xs font-medium rounded-2xl flex items-center gap-1`}>
                            <RiCheckLine className="h-4 w-4 text-gray-600"/> NO AOG
                          </span>
                          }
                      </div>

                      
                      <div className="rounded-xl mt-3">
                        <h6 className="text-sm text-gray-500 font-[500] mb-2">Amenities</h6>

                        {/* OLD Amenities
                        <div className="flex flex-wrap gap-2 mt-2">
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_wifi ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} WiFi 
                        </span>
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_entertainment_system ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} Entertainment System 
                        </span>
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_full_gallery ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} Full Gallery 
                        </span>
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_enclosed_lavatory ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} Enclosed Lavatory 
                        </span>
                        <span className={`flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200`}>
                          {amenities?.has_satellite_phone ? <RiCheckLine className="h-4 w-4" /> : <RiCloseLine className="h-4 w-4" />} Satellite Phone 
                        </span>
                        {amenities?.additional_amenities?.length > 0 &&
                          amenities.additional_amenities.map((amenity, idx) => (
                            <span key={idx} className="flex px-2 py-1 text-xs rounded-2xl border bg-gray-100 text-black border-gray-200">
                              <RiCheckLine className="h-4 w-4" /> {amenity}
                            </span>
                          ))
                        } 
                        </div>
                        */}

                        {features?.custom_features && features?.custom_features?.length > 0 ? (
                            <div className="grid grid-cols-1 gap-1">
                              {features?.custom_features.map((feature, index) => (
                                <div key={index} className="flex border-b py-1">
                                  <div className="w-10 h-7 flex items-center justify-center flex-shrink-0">
                                    <RiCheckLine className="w-5 h-5 text-gray-500" />
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-500">{feature}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                        ): (
                          <p className="text-sm text-gray-400">No amenities included.</p>
                        )}

    
                    </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="d-block pr-4 pb-5">
            <p className="text-sm bg-gray-100 text-gray-900 px-5 py-4 rounded-full">
              This quote is valid until {formatLongDate(settings.validUntil)}. Prices are subject to availability and may change.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-3 pb-16 bg-white flex-shrink-0">
          <div className="flex gap-2">
            {!hideSaveDraft && (
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSending || isSavingDraft}
                className={`${canSendQuote ? 'px-4' : 'flex-1 px-3'} py-2 text-sm border border-gray-300 text-gray-700 rounded-3xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 font-medium`}
              >
                {isSavingDraft ? (
                  <>
                    <RiLoader4Line className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <RiDraftLine className="h-4 w-4"/>
                    Save Draft
                  </>
                )}
              </button>
            )}
            {canSendQuote && (
              <button
                type="button"
                onClick={handleSend}
                disabled={isSending || isSavingDraft}
                className="flex-1 px-3 py-2 text-sm bg-primary-500 text-white rounded-3xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 font-medium"
              >
                {isSending ? (
                  <>
                    <RiLoader4Line className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <RiSendPlane2Line className="h-4 w-4"/>
                    Send Quote to Client
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default QuotePreview;
